import type { messages as ptMessages } from './pt-BR';

/**
 * The English fallback. `pt-BR.ts` owns the key set; the `Record<keyof
 * typeof ptMessages, string>` annotation below is what turns a missing (or
 * misspelled) key here into a compile error rather than a runtime hole, and
 * `src/i18n/__tests__/i18n.test.ts` re-checks the same guarantee at the value
 * level so a `// @ts-expect-error`-suppressed gap can't slip through either.
 *
 * The `estimator.statement.N` values are platform-design §6.6's normative
 * text, copied verbatim, and are pinned to the specification rather than to
 * the `estimator.statement.N.source` copies beside them: see
 * `__tests__/fixtures/platform-design-6.6.md` and the digest that holds it.
 */
export const messages: Record<keyof typeof ptMessages, string> = {
  // -- Shell chrome --------------------------------------------------------
  'shell.appName': 'SocialSrc',
  'shell.loading': 'Loading…',
  'shell.itemCount.one': '{count} item',
  'shell.itemCount.other': '{count} items',
  'notFound.title': 'Page not found',
  'notFound.body': 'That address does not match any page in this application.',
  'notFound.home': 'Back to home',

  // -- Auth flow ------------------------------------------------------------
  'auth.signIn': 'Sign in',
  'auth.signUp': 'Create account',
  'auth.name': 'Name',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.password.hint': 'At least 8 characters.',
  'auth.submit': 'Sign in',
  'auth.or': 'or',
  'auth.error.invalidCredentials': 'Invalid email or password.',
  'auth.error.generic': "Couldn't sign in. Try again.",
  'auth.error.emailInUse': 'This email is already in use.',
  'auth.error.weakPassword': 'That password is too weak. Use at least 6 characters.',
  'auth.error.network': 'Connection problem. Check your internet and try again.',
  'auth.error.tooManyRequests': 'Too many attempts. Wait a moment and try again.',
  'auth.error.popupClosed': 'The Google window closed before finishing. Try again.',
  'auth.error.accountExists':
    'This email already has a password account. Sign in with the email and password.',
  'auth.google': 'Continue with Google',
  'auth.signUp.prompt': 'First time here?',
  'auth.signIn.prompt': 'Already have an account?',
  'auth.forgotPassword': 'Forgot my password',
  'auth.back': 'Back',
  'auth.resetPassword.heading': 'Recover password',
  'auth.resetPassword.submit': 'Send the link',
  'auth.resetPassword.success': 'If that email has an account, we sent a reset link.',
  'auth.resetPassword.error': "Couldn't send the email. Try again.",
  'auth.resetPassword.sentTo': 'Link sent to {email}',
  'auth.resetPassword.linkExpiry': 'The link expires in 1 hour.',
  'auth.resetPassword.tooManyAttempts': 'Too many attempts. Wait a moment and try again.',
  'auth.resetPassword.resend': 'Send another link',

  // -- Common -----------------------------------------------------------------
  'common.retry': 'Retry',

  // -- §6.6 burn-rate estimator: normative text, verbatim from the spec.
  'estimator.statement.1':
    'Comment cost is driven by comment volume, which you do not control. The estimate uses the ' +
    "account's recent average; a post that goes viral costs in proportion to its thread. The panel " +
    'prices the tail scenario, p90 or a figure the tenant enters, with and without the per-post cap, ' +
    'so the cap is set with a number in view.',
  'estimator.statement.1.source':
    'Comment cost is driven by comment volume, which you do not control. The estimate uses the ' +
    "account's recent average; a post that goes viral costs in proportion to its thread. The panel " +
    'prices the tail scenario, p90 or a figure the tenant enters, with and without the per-post cap, ' +
    'so the cap is set with a number in view.',

  'estimator.statement.2':
    'Comment cost depends on page utilisation. Pulling every round on a busy post buys latency, not ' +
    'more comments. The panel shows utilisation per segment and the floor per post.',
  'estimator.statement.2.source':
    'Comment cost depends on page utilisation. Pulling every round on a busy post buys latency, not ' +
    'more comments. The panel shows utilisation per segment and the floor per post.',

  'estimator.statement.3':
    'The first segment sets a floor. The newest page is polled at that tier whether or not anything ' +
    'is on it; this is your new-post detection latency.',
  'estimator.statement.3.source':
    'The first segment sets a floor. The newest page is polled at that tier whether or not anything ' +
    'is on it; this is your new-post detection latency.',

  'estimator.statement.4':
    "Every fetch is live and fully billed, whatever the interval: the provider's cache is never " +
    'used to answer a scheduled pull.',
  'estimator.statement.4.source':
    "Every fetch is live and fully billed, whatever the interval: the provider's cache is never " +
    'used to answer a scheduled pull.',
};
